import React, { useState, useEffect, useCallback } from 'react';
import axios from '../config/axios';
import '../assets/styles/PayoutManager.css';

const PayoutManager = ({ householdId }) => {
  const [payoutSummary, setPayoutSummary] = useState([]);
  const [payoutHistory, setPayoutHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showPayoutModal, setShowPayoutModal] = useState(false);
  const [selectedKid, setSelectedKid] = useState(null);
  const [payoutForm, setPayoutForm] = useState({
    amount: '',
    payment_method: 'cash',
    notes: ''
  });

  const fetchData = useCallback(async () => {
    try {
      const [summaryRes, historyRes] = await Promise.all([
        axios.get(`/api/v1/payouts/summary?household_id=${householdId}`),
        axios.get(`/api/v1/payouts?household_id=${householdId}`)
      ]);

      setPayoutSummary(summaryRes.data || []);
      setPayoutHistory(historyRes.data || []);
      setError(null);
    } catch (err) {
      console.error('Failed to fetch payout data:', err);
      setError('Failed to load payout information');
    } finally {
      setLoading(false);
    }
  }, [householdId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handlePayoutClick = (kid) => {
    setSelectedKid(kid);
    setPayoutForm({
      amount: '',
      payment_method: 'cash',
      notes: ''
    });
    setShowPayoutModal(true);
  };

  const handlePayoutSubmit = async (e) => {
    e.preventDefault();

    const amountInCents = Math.round(parseFloat(payoutForm.amount) * 100);

    if (amountInCents <= 0) {
      alert('Please enter a valid amount');
      return;
    }

    if (amountInCents > selectedKid.current_balance) {
      alert(`Cannot pay out more than available balance ($${(selectedKid.current_balance / 100).toFixed(2)})`);
      return;
    }

    try {
      await axios.post(
        '/api/v1/payouts',
        {
          kid_user_id: selectedKid.kid_user_id,
          amount: amountInCents,
          payment_method: payoutForm.payment_method,
          notes: payoutForm.notes
        }
      );

      setShowPayoutModal(false);
      fetchData();
      alert(`Successfully recorded $${payoutForm.amount} payment to ${selectedKid.kid_name}!`);
    } catch (err) {
      console.error('Failed to record payout:', err);
      alert(err.response?.data || 'Failed to record payout');
    }
  };

  const formatCurrency = (cents) => {
    return `$${(cents / 100).toFixed(2)}`;
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  if (loading) {
    return <div className="payout-loading">Loading payout information...</div>;
  }

  if (error) {
    return <div className="error-message">{error}</div>;
  }

  return (
    <div className="payout-manager">
      <div className="payout-header">
        <h2>💰 Cash Payouts</h2>
        <p className="payout-subtitle">Track when you pay kids in real cash outside the app</p>
      </div>

      {/* Summary Cards */}
      <div className="payout-summary-grid">
        {payoutSummary.map(kid => (
          <div key={kid.kid_user_id} className="payout-summary-card">
            <div className="kid-header">
              <div className="kid-avatar">{kid.avatar || '👤'}</div>
              <div className="kid-info">
                <h3>{kid.kid_name}</h3>
                <div className="balance-info">
                  <span className="label">Owes:</span>
                  <span className={`amount ${kid.amount_owed > 0 ? 'owed' : ''}`}>
                    {formatCurrency(kid.amount_owed)}
                  </span>
                </div>
              </div>
            </div>

            <div className="kid-stats">
              <div className="stat">
                <span className="stat-label">Total Paid Out</span>
                <span className="stat-value">{formatCurrency(kid.total_paid_out)}</span>
              </div>
              <div className="stat">
                <span className="stat-label">Lifetime Earned</span>
                <span className="stat-value">{formatCurrency(kid.lifetime_earned)}</span>
              </div>
            </div>

            {kid.amount_owed > 0 && (
              <button
                className="btn-payout"
                onClick={() => handlePayoutClick(kid)}
              >
                💵 Record Payment
              </button>
            )}
            {kid.amount_owed === 0 && (
              <div className="paid-up-badge">✅ All Paid Up!</div>
            )}
          </div>
        ))}
      </div>

      {payoutSummary.length === 0 && (
        <div className="no-data">
          <p>No kids in household yet</p>
        </div>
      )}

      {/* Payment History */}
      {payoutHistory.length > 0 && (
        <div className="payout-history">
          <h3>📋 Payment History</h3>
          <div className="history-list">
            {payoutHistory.map(payout => (
              <div key={payout.id} className="history-item">
                <div className="history-icon">💵</div>
                <div className="history-content">
                  <div className="history-main">
                    <span className="kid-name">{payout.kid_name}</span>
                    <span className="amount">{formatCurrency(payout.amount)}</span>
                  </div>
                  <div className="history-details">
                    <span className="method">{payout.payment_method}</span>
                    <span className="date">{formatDate(payout.paid_at)}</span>
                  </div>
                  {payout.notes && (
                    <div className="history-notes">"{payout.notes}"</div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Payout Modal */}
      {showPayoutModal && selectedKid && (
        <div className="modal-overlay" onClick={() => setShowPayoutModal(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <h2>Record Cash Payment</h2>
            <div className="modal-kid-info">
              <span className="kid-avatar">{selectedKid.avatar}</span>
              <span className="kid-name">{selectedKid.kid_name}</span>
            </div>
            <div className="available-balance">
              Available Balance: <strong>{formatCurrency(selectedKid.current_balance)}</strong>
            </div>

            <form onSubmit={handlePayoutSubmit}>
              <div className="form-group">
                <label>Amount ($)</label>
                <input
                  type="number"
                  step="0.01"
                  min="0.01"
                  max={selectedKid.current_balance / 100}
                  value={payoutForm.amount}
                  onChange={(e) => setPayoutForm({ ...payoutForm, amount: e.target.value })}
                  placeholder="0.00"
                  required
                  autoFocus
                />
              </div>

              <div className="form-group">
                <label>Payment Method</label>
                <select
                  value={payoutForm.payment_method}
                  onChange={(e) => setPayoutForm({ ...payoutForm, payment_method: e.target.value })}
                >
                  <option value="cash">Cash</option>
                  <option value="venmo">Venmo</option>
                  <option value="zelle">Zelle</option>
                  <option value="paypal">PayPal</option>
                  <option value="check">Check</option>
                  <option value="other">Other</option>
                </select>
              </div>

              <div className="form-group">
                <label>Notes (optional)</label>
                <textarea
                  value={payoutForm.notes}
                  onChange={(e) => setPayoutForm({ ...payoutForm, notes: e.target.value })}
                  placeholder="e.g., Paid at dinner, ATM cash, etc."
                  rows="3"
                />
              </div>

              <div className="modal-actions">
                <button type="button" className="btn-cancel" onClick={() => setShowPayoutModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn-submit">
                  Record ${payoutForm.amount || '0.00'} Payment
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default PayoutManager;
